/**
 * Pure activity-timeline derivations for the Contact Edit Modal.
 *
 * Groups the contact's activities into month buckets for the timeline view.
 * Extracted verbatim from `ContactEditModal.tsx` (behavior-preserving — #3237).
 */

import type { Activity } from "./types";

/**
 * Determines if an email was sent by the user or received.
 * @param fromEmail - The sender email address (normalized to lowercase)
 * @param userEmails - Array of user's own email addresses (normalized to lowercase)
 * @param contactEmail - Contact's email address (normalized to lowercase), for reference
 * @returns true if sent by user, false if received
 */
export function determineEmailDirection(
  fromEmail: string,
  userEmails: string[],
  contactEmail?: string,
): boolean {
  const isFromMe = userEmails.some((ue) => fromEmail.includes(ue));
  const isFromContact = contactEmail ? fromEmail.includes(contactEmail) : false;
  return isFromMe && !isFromContact;
}

/**
 * Detect automated/transactional senders (not human conversations).
 * Includes Google Drive/Docs share-notification senders (issue #3494),
 * which would otherwise be misattributed in the contact activity timeline.
 * @param from - The sender email address (any case)
 * @returns true if the sender is automated/transactional
 */
export function isAutomatedSender(from: string): boolean {
  const f = from.toLowerCase();
  return (
    /\b(no-?reply|do-?not-?reply|noreply|notifications?|mailer-daemon|bounce|postmaster|auto-confirm|shipment-tracking|order-update)\b/.test(
      f,
    ) ||
    /\b(news|newsletter|marketing|promo|digest|updates?|billing|receipts?|invoices?|alerts?)\s*@/.test(
      f,
    ) ||
    /drive-shares[^@]*@google\.com|docs\.google\.com|drive-shares-noreply/i.test(
      f,
    )
  );
}

export interface ActivityGroup {
  key: string;
  label: string;
  items: Activity[];
}

/**
 * Group activities into year-month buckets, preserving original ordering.
 * Mirrors the inline IIFE that previously lived in the modal body.
 */
export function groupActivitiesByMonth(
  activities: Activity[],
): ActivityGroup[] {
  const groups: { key: string; label: string; items: Activity[] }[] = [];
  const map = new Map<string, Activity[]>();
  for (const a of activities) {
    const d = new Date(a.date);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const label = d
      .toLocaleDateString("en-US", { month: "long", year: "numeric" })
      .toUpperCase();
    if (!map.has(key)) {
      map.set(key, []);
      groups.push({ key, label, items: map.get(key)! });
    }
    map.get(key)!.push(a);
  }
  return groups;
}
